import React, { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import BookletViewer from './components/BookletViewer';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const EXTRACTED_BASE = `${API_BASE.replace(/\/+$|$/, '')}/extracted`;

const fileType = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'ogg', 'mov', 'm4v'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'aac'].includes(ext)) return 'audio';
  if (['html', 'htm', 'xhtml'].includes(ext)) return 'html';
  if (['txt', 'css', 'js', 'json', 'xml'].includes(ext)) return 'text';
  return 'other';
};

function BrowserApp() {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [contents, setContents] = useState([]);
  const [currentDir, setCurrentDir] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const onChange = (event) => {
    setFile(event.target.files[0]);
    setMessage('');
    setError('');
  };

  const loadDirectory = async (dir = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/extracted/list?dir=${encodeURIComponent(dir)}`);
      if (!response.ok) {
        throw new Error('Failed to load directory');
      }
      const data = await response.json();
      setContents(data.contents || []);
      setCurrentDir(data.dir || '');
      setSelectedFile(null);
      setPreviewText('');
    } catch (err) {
      setError(err.message || 'Could not load extracted directory');
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setError('Please select an EPUB file.');
      return;
    }

    const formData = new FormData();
    formData.append('epub', file);
    setIsUploading(true);

    try {
      const response = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.error || 'Upload failed');
      }

      const data = await response.json();
      setMessage(data.message);
      setError('');
      loadDirectory('');
    } catch (err) {
      setMessage('');
      setError(err.message || 'Upload error');
    } finally {
      setIsUploading(false);
    }
  };

  const openBooklet = (dir) => {
    const bookId = dir.split('/')[0] || dir;
    if (bookId) {
      navigate(`/booklet/${encodeURIComponent(bookId)}`);
    }
  };

  const onSelectItem = async (item) => {
    if (item.isDirectory) {
      if (!currentDir) {
        openBooklet(item.path);
        return;
      }
      loadDirectory(item.path);
      return;
    }

    if (item.name.match(/\.(x?html?|htm)$/i)) {
      const bookId = currentDir.split('/')[0] || currentDir;
      if (bookId) {
        navigate(`/booklet/${encodeURIComponent(bookId)}`);
        return;
      }
    }

    setSelectedFile(item.path);
    setPreviewText('');

    if (fileType(item.name) === 'text') {
      try {
        const response = await fetch(`${EXTRACTED_BASE}/${encodeURI(item.path)}`);
        if (!response.ok) {
          throw new Error('Failed to load file preview');
        }
        const text = await response.text();
        setPreviewText(text);
      } catch (err) {
        setError(err.message || 'Could not preview file');
      }
    }
  };

  const renderPreview = () => {
    if (!selectedFile) {
      return <p>Select a file to preview.</p>;
    }

    const url = `${EXTRACTED_BASE}/${encodeURI(selectedFile)}`;
    const type = fileType(selectedFile);

    if (type === 'image') {
      return <img src={url} alt={selectedFile} className="preview-image" />;
    }
    if (type === 'video') {
      return (
        <video className="preview-video">
          <source src={url} />
          Your browser does not support the video tag.
        </video>
      );
    }
    if (type === 'audio') {
      return <audio controls src={url} className="preview-audio" />;
    }
    if (type === 'html') {
      return <iframe title="HTML Preview" src={url} className="preview-iframe" />;
    }
    if (type === 'text') {
      return <pre className="preview-text">{previewText}</pre>;
    }

    return (
      <div>
        <p>No inline preview available for this file type.</p>
        <a href={url} target="_blank" rel="noreferrer">Open file</a>
      </div>
    );
  };

  useEffect(() => {
    const password = prompt("Please enter password:");
    if (password === "systech.12") {
      setIsAuthenticated(true);
      loadDirectory('');
    } else {
      alert("Incorrect password!");
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'red' }}>
        <h2>Access Restricted</h2>
        <p>Please refresh the page and enter the correct password.</p>
      </div>
    );
  }

  const currentBookId = currentDir.split('/')[0] || currentDir;

  return (
    <div className="app-shell">
      <header style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <img src="/brochure.png" alt="Ebrochure Logo" style={{ width: '64px', height: '64px', objectFit: 'contain' }} />
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '2.25rem' }}>EPUB Viewer</h1>
          <p style={{ margin: 0 }}>Upload an EPUB, optimize media, and browse extracted files.</p>
        </div>
      </header>

      <main>
        <section className="upload-form">
          <form onSubmit={onSubmit}>
            <label htmlFor="epub">Select EPUB</label>
            <input type="file" id="epub" accept=".epub" onChange={onChange} />
            <button type="submit" disabled={isUploading || !file}>
              {isUploading ? 'Uploading & Extracting...' : 'Upload & Extract'}
            </button>
          </form>

          {message && <div className="status success">{message}</div>}
          {error && <div className="status error">{error}</div>}
        </section>

        <section className="browser-panel">
          <div className="browser-list">
            <div className="browser-header">
              <div>
                <strong>Browse extracted files</strong>
                <span>{currentDir || '/'}</span>
              </div>
              {currentBookId && (
                <button type="button" className="booklet-open" onClick={() => openBooklet(currentBookId)}>
                  Open booklet view
                </button>
              )}
            </div>
            {contents.length === 0 ? (
              <p>No extracted content found.</p>
            ) : (
              <ul>
                {currentDir && (
                  <li>
                    <button type="button" onClick={() => loadDirectory(currentDir.split('/').slice(0, -1).join('/'))}>
                      .. (up)
                    </button>
                  </li>
                )}
                {contents.map((item) => (
                  <li key={item.path}>
                    <button type="button" onClick={() => onSelectItem(item)}>
                      {item.isDirectory ? '📁' : '📄'} {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="browser-preview">
            <h2>Preview</h2>
            {renderPreview()}
          </div>
        </section>
      </main>

      <footer style={{ textAlign: 'center', padding: '24px 0 8px 0', marginTop: '32px', borderTop: '1px solid #e2e8f0', fontSize: '0.875rem', color: '#64748b' }}>
        DESIGNED & MANAGED BY <a href="https://systechlogic.com/" target="_blank" rel="noopener noreferrer" className="footer-brand-link" style={{ color: '#4f46e5' }}>SYSTECHLOGIC</a>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<BrowserApp />} />
        <Route path="/booklet/:bookId" element={<BookletViewer />} />
        <Route path="*" element={<BrowserApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
